import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import CancelButton from "@/components/buttons/CancelButton";
import { Field } from "@/components/ui/field";
import { Alert } from "@/components/ui/alert";
import { useI18n } from "@/i18n/I18nContext";
import { Group, Heading, Textarea } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

interface ApprovalRejectDialogProps {
  heading: string;
  isOpen: boolean;
  onClose: () => void;
  onReject: (reason: string) => Promise<void>;
}

const ApprovalRejectDialog = ({ heading, isOpen, onClose, onReject }: ApprovalRejectDialogProps) => {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError(null);
    }
  }, [isOpen]);

  const handleReject = async (): Promise<void> => {
    if (!trimmedReason || inFlight.current) {
      return;
    }

    inFlight.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      await onReject(trimmedReason);
      setReason("");
      onClose();
    } catch {
      setError(t("approvals.decisionFailed"));
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const handleClose = () => { if (!inFlight.current) onClose(); };

  return (
    <DialogRoot open={isOpen} onOpenChange={handleClose} size="sm" placement="center">
      <DialogContent>
        <DialogHeader>
          <Heading size="md">{heading}</Heading>
        </DialogHeader>
        <DialogCloseTrigger disabled={isSubmitting} />
        <DialogBody>
          {error && <Alert status="error" role="alert" mb="3">{error}</Alert>}
          <Field label={t("approvals.decisionReason")} required>
            <Textarea
              autoFocus
              disabled={isSubmitting}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Begründung hinzufügen..."
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
            <CancelButton onClick={handleClose} disabled={isSubmitting} />
            <Button
              type="button"
              colorPalette="orange"
              variant="outline"
              loading={isSubmitting}
              disabled={!trimmedReason || isSubmitting}
              onClick={handleReject}
            >
              {t("approvals.actions.reject")}
            </Button>
          </Group>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default ApprovalRejectDialog;
